/**
 * useCalendarPosts Hook
 *
 * Custom hook for fetching all posts for calendar view with filtering options
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { fetchAllBlogPosts } from "../api/supabase";

/**
 * Hook for fetching all posts for calendar view
 * @param {Object} filters - Filters to apply
 * @param {number} pollInterval - Polling interval in ms (default: 30000)
 * @returns {{posts: Array, loading: boolean, error: string|null, refetch: Function}}
 */
export const useCalendarPosts = (filters = {}, pollInterval = 30000) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const timeoutRef = useRef(null);
  const isMountedRef = useRef(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await fetchAllBlogPosts();

      if (isMountedRef.current) {
        // Apply filters
        let filteredData = data;

        if (filters.status && filters.status !== "all") {
          filteredData = filteredData.filter((post) => post.status === filters.status);
        }

        if (filters.dateRange) {
          const { start, end } = filters.dateRange;
          filteredData = filteredData.filter((post) => {
            const postDate = new Date(post.date || post.created_at);
            return postDate >= start && postDate <= end;
          });
        }

        setPosts(filteredData);
      }
    } catch (err) {
      console.error("Error fetching calendar posts:", err);
      if (isMountedRef.current) {
        setError(err.message);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [filters]);

  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    isMountedRef.current = true;

    // Initial fetch
    fetchData();

    // Set up polling
    const schedulePoll = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        fetchData();
        schedulePoll();
      }, pollInterval);
    };

    schedulePoll();

    // Cleanup
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [fetchData, pollInterval]);

  return {
    posts,
    loading,
    error,
    refetch,
  };
};

export default useCalendarPosts;
